import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogActions, DialogContent, DialogTitle, TablePagination } from "@material-ui/core";
import { Link } from "react-router-dom";

import TablePaginationActions from "../../component/table/TablePagination";
import { Toast } from "../../util/Toast";
import { FEATURES, useAccess } from "../../hooks/useAccess";

const STATUSES = ["pending", "auto_rejected", "approved", "rejected", "expired"];
const statusClass = {
  pending: "badge bg-warning text-dark",
  auto_rejected: "badge bg-danger",
  approved: "badge bg-success",
  rejected: "badge bg-secondary",
  expired: "badge bg-dark",
};

const userLabel = (user) => user?.username || user?.name || user?.uniqueId || "Deleted user";
const userMeta = (user) => user?.uniqueId || user?.email || "—";
const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "—";

const UserSummary = ({ user, title }) => (
  <div className="border rounded p-3 h-100" style={{ color: "#f8f9fa" }}>
    <small className="text-muted d-block mb-1">{title}</small>
    <strong style={{ color: "#f8f9fa" }}>{userLabel(user)}</strong>
    <div className="small text-muted">{userMeta(user)}</div>
    {user?.email && <div className="small text-muted text-break">{user.email}</div>}
    {user?.image && (
      <img
        className="mt-2 rounded-circle"
        src={user.image}
        alt={`${userLabel(user)} profile`}
        style={{ width: 52, height: 52, objectFit: "cover" }}
      />
    )}
  </div>
);

const LivenessFaceReviews = () => {
  const { canEdit, isSuperAdmin } = useAccess();
  const editable = isSuperAdmin || canEdit(FEATURES.BACKEND_FACE_REVIEWS);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [candidateImageUrl, setCandidateImageUrl] = useState("");
  const [candidateImageError, setCandidateImageError] = useState("");
  const [matchedImageUrl, setMatchedImageUrl] = useState("");
  const [matchedImageError, setMatchedImageError] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get("liveness/admin/face-reviews", {
        params: { status: status || undefined, page: pagination.page, limit: pagination.limit },
      });
      const data = response.data?.data || {};
      setReviews(data.reviews || []);
      setPagination((current) => ({ ...current, ...(data.pagination || {}) }));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Unable to load face reviews.");
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, status]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    if (!selected) {
      setCandidateImageUrl("");
      setCandidateImageError("");
      setMatchedImageUrl("");
      setMatchedImageError("");
      return undefined;
    }

    let active = true;
    const objectUrls = [];
    setCandidateImageUrl("");
    setCandidateImageError("");
    setMatchedImageUrl("");
    setMatchedImageError("");

    const loadProtectedImage = async (path, setUrl, setError, unavailableMessage) => {
      try {
        const response = await axios.get(path, { responseType: "blob" });
        if (!active) return;
        const objectUrl = URL.createObjectURL(response.data);
        objectUrls.push(objectUrl);
        setUrl(objectUrl);
      } catch (_requestError) {
        if (active) setError(unavailableMessage);
      }
    };

    loadProtectedImage(
      `liveness/admin/face-reviews/${selected._id}/image`,
      setCandidateImageUrl,
      setCandidateImageError,
      "The candidate liveness image is unavailable or has expired."
    );
    loadProtectedImage(
      `liveness/admin/face-reviews/${selected._id}/matched-image`,
      setMatchedImageUrl,
      setMatchedImageError,
      "The matched enrollment image is unavailable. It may have been created before enrollment-image retention was enabled."
    );

    return () => {
      active = false;
      objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, [selected]);

  const openReview = (review) => {
    setSelected(review);
    setNotes(review.reviewNotes || "");
  };

  const closeReview = () => {
    if (!saving) setSelected(null);
  };

  const decideReview = async (decision) => {
    if (!selected || !editable) return;
    setSaving(true);
    try {
      const response = await axios.patch(`liveness/admin/face-reviews/${selected._id}`, { decision, notes });
      const updated = response.data?.data;
      setReviews((current) => current.map((review) => (review._id === updated?._id ? updated : review)));
      setSelected(null);
      Toast("success", decision === "approve" ? "Liveness verification approved." : "Duplicate liveness attempt rejected.");
      loadReviews();
    } catch (requestError) {
      Toast("error", requestError.response?.data?.message || requestError.message || "Unable to save the review.");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = (event) => {
    setStatus(event.target.value);
    setPagination((current) => ({ ...current, page: 1 }));
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last"><h3 className="mb-2 text-white">Liveness Face Reviews</h3></div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
              <ol className="breadcrumb"><li className="breadcrumb-item"><Link to="/admin/dashboard" className="text-danger">Dashboard</Link></li><li className="breadcrumb-item active">Liveness Face Reviews</li></ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header pb-0">
          <div className="row align-items-end my-2">
            <div className="col-12 col-md-4">
              <label htmlFor="face-review-status" className="form-label">Review status</label>
              <select id="face-review-status" className="form-control" value={status} onChange={changeStatus}>
                <option value="">All statuses</option>
                {STATUSES.map((item) => <option key={item} value={item}>{item.replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-4 mt-3 mt-md-0"><button type="button" className="btn btn-outline-danger" onClick={loadReviews}>Refresh</button></div>
          </div>
        </div>
        <div className="card-body card-overflow">
          {error && <div className="alert alert-danger" role="alert">{error} <button type="button" className="btn btn-sm btn-outline-light ms-2" onClick={loadReviews}>Retry</button></div>}
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead className="text-center"><tr><th>Submitted</th><th>Candidate account</th><th>Matched account</th><th>Similarity Score</th><th>Status</th><th>Review</th></tr></thead>
              <tbody className="text-center">
                {loading ? <tr><td colSpan="6" className="py-5"><span className="spinner-border spinner-border-sm text-danger me-2" />Loading face reviews…</td></tr>
                  : reviews.length === 0 ? <tr><td colSpan="6" className="py-5">No face reviews found.</td></tr>
                    : reviews.map((review) => <tr key={review._id}>
                      <td><small>{formatDateTime(review.createdAt)}</small></td>
                      <td><strong>{userLabel(review.candidateUser)}</strong><br /><small className="text-muted">{userMeta(review.candidateUser)}</small></td>
                      <td><strong>{userLabel(review.matchedUser)}</strong><br /><small className="text-muted">{userMeta(review.matchedUser)}</small></td>
                      <td><strong>{Number(review.similarity).toFixed(2)}%</strong></td>
                      <td><span className={statusClass[review.status] || "badge bg-secondary"}>{review.status.replace("_", " ")}</span></td>
                      <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => openReview(review)}>{editable && review.status === "pending" ? "Review" : "View"}</button></td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <TablePagination component="div" count={pagination.total || 0} page={Math.max(0, (pagination.page || 1) - 1)} rowsPerPage={pagination.limit || 20}
            rowsPerPageOptions={[10, 20, 50, 100]} onChangePage={(_event, page) => setPagination((current) => ({ ...current, page: page + 1 }))}
            onChangeRowsPerPage={(event) => setPagination((current) => ({ ...current, page: 1, limit: Number(event.target.value) }))} ActionsComponent={TablePaginationActions} />
        </div>
      </div>

      <Dialog open={Boolean(selected)} onClose={closeReview} fullWidth maxWidth="md" aria-labelledby="face-review-title">
        <DialogTitle id="face-review-title" style={{ color: "#f8f9fa" }}>Liveness face-match review</DialogTitle>
        <DialogContent style={{ color: "#f8f9fa" }}>
          {selected && <>
            <div className="row g-3 mb-3"><div className="col-12 col-md-6"><UserSummary title="Candidate account" user={selected.candidateUser} /></div><div className="col-12 col-md-6"><UserSummary title="Matched verified account" user={selected.matchedUser} /></div></div>
            <div className="alert alert-warning"><strong>{Number(selected.similarity).toFixed(2)}% similarity.</strong></div>
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6"><label className="form-label d-block" style={{ color: "#f8f9fa" }}>Candidate liveness image</label>
                {candidateImageUrl && <img src={candidateImageUrl} alt="Candidate liveness capture" className="img-fluid rounded border" style={{ maxHeight: 360 }} />}
                {!candidateImageUrl && !candidateImageError && <div className="text-muted">Loading protected liveness image…</div>}
                {candidateImageError && <div className="alert alert-secondary mb-0">{candidateImageError}</div>}
              </div>
              <div className="col-12 col-md-6"><label className="form-label d-block" style={{ color: "#f8f9fa" }}>Matched enrollment image</label>
                {matchedImageUrl && <img src={matchedImageUrl} alt="Matched account enrollment capture" className="img-fluid rounded border" style={{ maxHeight: 360 }} />}
                {!matchedImageUrl && !matchedImageError && <div className="text-muted">Loading protected enrollment image…</div>}
                {matchedImageError && <div className="alert alert-secondary mb-0">{matchedImageError}</div>}
              </div>
            </div>
            <label htmlFor="face-review-notes" className="form-label" style={{ color: "#f8f9fa" }}>Review notes (optional)</label>
            <textarea id="face-review-notes" className="form-control text-white" rows="4" maxLength="2000" value={notes} disabled={!editable || saving || selected.status !== "pending"} onChange={(event) => setNotes(event.target.value)} />
            {selected.reviewedBy && <small className="text-muted d-block mt-2">Reviewed by {selected.reviewedBy.name || selected.reviewedBy.email} on {formatDateTime(selected.reviewedAt)}.</small>}
          </>}
        </DialogContent>
        <DialogActions>
          <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={closeReview}>Close</button>
          {editable && selected?.status === "pending" && <><button type="button" className="btn btn-outline-danger" disabled={saving} onClick={() => decideReview("reject")}>{saving ? "Saving…" : "Reject duplicate"}</button><button type="button" className="btn btn-success" disabled={saving || Boolean(candidateImageError)} onClick={() => decideReview("approve")}>{saving ? "Saving…" : "Approve"}</button></>}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LivenessFaceReviews;
